<template>
  <figure
    ref="chrome"
    class="browser-frame"
    :class="'browser-frame--' + tone"
    :style="frameStyle"
  >
    <div class="browser-frame-bar">
      <span class="browser-frame-dots" aria-hidden="true"><i></i><i></i><i></i></span>
      <span class="browser-frame-address">
        <font-awesome-icon v-if="secure" icon="lock" class="browser-frame-lock" aria-hidden="true" />
        <span v-if="secure" class="browser-frame-scheme">https://</span>
        <span class="browser-frame-url">{{ url }}</span>
      </span>

      <!-- Whether what the frame holds is the site itself or a picture of it.

           The two states are told apart by what the view holds, but only once
           the reader has looked into it: the chrome says which site the window
           is on and nothing about whether that site is running. The badge says
           it in the one place the bar has left, at the far end from the address,
           so the window reads the way a browser's does, where you are on the
           left and what is happening on the right.

           It is drawn only when there is a page in the frame. A still is a
           picture of a site that was live when the picture was taken, and a
           badge over one would be claiming something about the frame that is
           not true of it. The live band passes `src` and gets the badge; a
           story chapter, which passes a screenshot and no page, does not.

           The word is the frame's own copy like the guard's, so it is looked up
           here rather than passed in. It takes the label style the empty slot
           already uses, with one step up: it is bold where that label is not.
           That is the one place this weight steps up on the site, and the badge
           is the reason it is allowed: a status whose whole job is to be noticed
           beside an address that is meant to be ignored cannot be set at the
           weight of the thing it is contrasting with. -->
      <span v-if="src" class="browser-frame-live">
        <status-dot />
        {{ t('Live') }}
      </span>
    </div>

    <!-- The frame's own edge is the one boundary the parent document can still
         see. A page inside a frame swallows its own pointer events, so while the
         pointer is over the client's site there is nothing here to listen to; the
         moment it is not is the moment this element is left, and that is the
         signal to put the lock back. It reads as "the pointer left the framed
         page" rather than "the pointer left the card": the chrome bar above is a
         sibling of this box, so moving up onto it counts as leaving too. -->
    <div class="browser-frame-view" @mouseleave="relock">
      <!-- With a `src` the screenshot is only the poster, so it is decorative
           and hidden from assistive tech: the frame is the content. Without one
           it is the content itself and takes the alt. -->
      <img
        v-if="image"
        class="browser-frame-poster"
        :src="image"
        :alt="src ? '' : imageAlt"
        :aria-hidden="src ? 'true' : undefined"
        loading="lazy"
        decoding="async"
      />

      <!-- A frame with no picture is a slot rather than a broken window. The
           case page's story chapters are all in that state until their
           screenshots are supplied, and a slot has to look like somewhere a
           picture goes: the chrome says which site it will hold, and this says
           that the view is still empty. It is a state of the frame, not a
           fallback, so it is drawn by the frame rather than by the caller that
           happens not to have a file yet.

           The slot itself is the shared component, because the device band
           waits for a picture too and a second box saying the same thing in its
           own words is how there come to be two placeholders on one page. All
           this wrapper decides is where the slot sits, which is the frame's
           business: inset from the window's edge, so the ground around it stays
           visible and the slot reads as sitting inside the window rather than
           being the window. -->
      <div v-else class="browser-frame-blank">
        <screenshot-slot />
      </div>

      <!-- The client's page is fetched only after the guard is lifted.

           The case study is an indexed page of this site. The address in the
           bar is the client's own site, and that site carries its own
           structured data. An iframe whose address is in the first render is a
           document Google reads as part of this page, which is how a product
           list on the client's site is reported against the case study. Until
           the frame is asked for, the view is the poster and nothing is
           requested. -->
      <iframe
        v-if="loaded"
        class="browser-frame-page"
        :src="src"
        :title="frameTitle"
        :tabindex="locked ? -1 : undefined"
        loading="lazy"
        referrerpolicy="no-referrer"
      ></iframe>

      <!-- The zoom affordance, for a still the reader can open at its own size.

           The target is the whole box rather than the mark, because the pointer
           lands somewhere on the picture it wants to look at more closely and
           asking it to find a small control first would be asking it to aim. So
           the box is the control and the mark is only what says so, which is
           also why the box carries `cursor: zoom-in`: a magnifier is the one
           cursor every reader already reads as "this will come closer".

           The mark is the site's reveal pattern, in the same shape the portfolio
           card uses: centred over the picture, hidden until the pointer is over
           it, and shown outright below lg, where there is no hover to reveal it
           with. It is drawn only when there is a picture and the caller has said
           the picture is worth opening; a frame holding a live page has nothing
           to enlarge, because the page is already at full size. -->
      <button
        v-if="zoomable && image"
        class="browser-frame-zoom"
        type="button"
        @click="$emit('zoom')"
      >
        <span class="browser-frame-zoom-mark">
          <font-awesome-icon icon="magnifying-glass" aria-hidden="true" />
          {{ t('View full size') }}
        </span>
      </button>

      <!-- The guard: the frame is inert until this is pressed. It is a sibling of
           the frame rather than a rule on it, because a cross-origin page cannot
           be reached from here and nothing this document sets on the frame's own
           scrolling can be taken back. A sheet over the box is the one thing that
           can always be withdrawn.

           The scrim is what makes the state legible: a live page behind plain
           glass reads as a page to use, and the pointer would try to use it.
           Darkening it says the box is switched off, and the button says what
           switches it on. -->
      <transition name="browser-frame-guard">
        <div v-if="src && locked" class="browser-frame-guard">
          <app-button variant="ghost" tone="dark" icon="lock" @click="unlock">
            {{ t('Click to unlock') }}
          </app-button>
        </div>
      </transition>
    </div>

    <!-- The foot, for the one thing a frame can hold that is not in it: what the
         view is showing. It is the second piece of chrome and it sits at the far
         end of the window from the bar, so the frame closes on a rule the way the
         address opens it. A frame with nothing to say about its view has no foot
         at all, which is the live band: its address already says which site it
         holds, and a note under a window that is the window would say it twice. -->
    <figcaption v-if="caption" class="browser-frame-foot">{{ caption }}</figcaption>
  </figure>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A site in browser chrome: the address bar and three dots the whole web reads
   as "this is a page", drawn here rather than screenshotted so it stays sharp at
   any width and does not become a picture of a browser that ages with one.

   The case page uses this for the live frame today, which puts the client's own
   address inside it. It also carries a still: with no `src` the screenshot is the
   whole of the card and describes itself through `imageAlt`, which is what the
   next page that wants a browser frame around a picture needs. The same bar,
   radius, crop and height either way, and the only difference is whether
   anything interactive is layered over the picture.

   And a still with no picture is the third state: chrome around an empty slot,
   which is what a screenshot that has not been supplied yet is drawn as. The
   height is the caller's on both axes, because the live band wants a window and
   a case story wants a crop beside a paragraph, and neither is this component's
   to decide.

   There is nothing interactive under the frame. A foot with the band's action in
   it used to hang off the bottom, and the one caller that ever passed it was the
   case page, whose button said what the address in the bar already says. The foot
   is back for the one thing that is not a repeat of the bar: what the view is
   showing. It arrives as the `caption` prop, and it is the caller's sentence
   rather than this component's, because it describes what the caller put in the
   frame the way `url` names it.

   A framed page is inert until the guard over it is pressed, and inert again the
   moment the pointer, a press or the focus goes somewhere else. That is the whole
   of the interaction a framed site gets: the frame is for looking at, and, when
   it is asked for, for using, and never for operating by accident. The lock and
   the four things that put it back are below the view, where the guard is.

   tone is the frame's own light or dark set, and it decides the chrome bar, the
   ink inside it and the shadow together: the case page stands a light frame on
   its dark band, where a dark bar would have had nothing to be contrasted
   against. */
.browser-frame {
  background: $ground-accent;
  border-radius: $r-sm;
  margin: 0 auto;
  overflow: hidden;

  /* The ceiling the View In control moves. It is a max-width rather than a width
     so that a viewport narrower than the chosen device still fits the frame, and
     it is transitioned so the frame resizes rather than jumping between the three
     widths. Only lengths can be interpolated, which is why the page passes a very
     wide length for its widest mode rather than "100%". */
  transition: max-width $dur-slow $ease;
}

/* The two grounds, which is what the tone prop chooses between. Each one names
   the chrome bar, the foot, the address ink and the connection marks
   together, because they are one decision: chrome is either the light set or the
   dark set, and a light bar carrying dark-ground ink is the drift this keeps out.

   The bar is a shade off the page below it, not the same fill. A window's title
   bar is a step from its content even in light mode, and without that step the
   bar and the framed page in the same near-white read as one flat field. The
   hairline does the rest on the light set, where the two fills are closest. The
   foot takes the bar's own fill and carries the same hairline, so the frame is
   bookended by one piece of chrome rather than by two different greys.

   The foot takes the address's ink as well, on both sets. The quiet greys the
   site uses for small print do not hold on this ground: $text-muted measures
   2.7:1 against the bar's fill and $text-label 4.4:1, both under the 4.5:1 a
   line this size needs, so the foot takes the step the address above it already
   uses rather than a lighter one that cannot be read. What keeps it quiet is its
   size and its place, not a paler grey.

   The two greens are the status family's two ends: $status-good-ink is the one
   tuned to be read as type on a pale ground, and $online-ink is the brighter one
   for a few pixels of fill on a dark one. The lock, the scheme and the live
   badge are all one decision - the bar is saying the connection is good - so
   they take whichever green matches the bar they stand on, which is why neither
   is the same green in both sets. The light inside the badge is not part of
   that choice: it takes $online-ink on both bars, because a light is fill
   rather than a letterform and has no contrast band to stay inside. */
.browser-frame--light {
  border: 1px solid $line;
  box-shadow: $shadow-lift;

  .browser-frame-bar {
    background: $ground-tint;
    border-bottom: 1px solid $line;
  }

  .browser-frame-foot {
    background: $ground-tint;
    border-top: 1px solid $line;
    color: $text-muted;
  }

  .browser-frame-address {
    color: $text-body;
  }

  .browser-frame-lock,
  .browser-frame-scheme,
  .browser-frame-live {
    color: $status-good-ink;
  }
}

.browser-frame--dark {
  box-shadow: $shadow-dark;

  .browser-frame-bar {
    background: $ground-dark;
  }

  /* No hairline on the dark set, matching the bar for the same reason: the value
     break against the pale view is the edge, and a rule between them would be a
     third line in a frame that already reads as two. */
  .browser-frame-foot {
    background: $ground-dark;
    color: $on-dark-muted;
  }

  .browser-frame-address {
    color: $on-dark-body;
  }

  .browser-frame-lock,
  .browser-frame-scheme,
  .browser-frame-live {
    color: $online-ink;
  }
}

.browser-frame-bar {
  align-items: center;
  display: flex;
  gap: 0.9rem;
  padding: 0.75rem 1rem;
}

/* What the view shows, said quietly at the frame's foot.

   It is body type rather than the bar's mono, because it is a sentence about a
   picture and not a machine's address. It is smaller than the prose the chapter
   is set in, which is the whole of the treatment: a foot that matched the
   reading would be read as the next paragraph, and one set in the label style
   would be read as a heading over the picture it describes.

   It is a further step under the site's other captions, because it is the
   quietest thing a case page says. A caption under a picture stands on its own
   and can be read at the caption scale the prose uses (0.9rem); this one is a
   second voice inside a figure that already has chrome, sitting under a view the
   reader has just looked at, so it has to fall below that scale to stay a note
   rather than an explanation of the picture above it. The file said this before
   it was true: the comment claimed the foot was kept quiet by its size while it
   was set at 0.8rem, which is the size of the address in the bar and only a
   step under the captions it is supposed to sit beneath.

   The ink comes down a step with the size, which reverses what this comment said
   when the size stepped down on its own. The earlier note argued the foot should
   be kept quiet by its size alone, because every paler step on this ground is
   under the 4.5:1 a line this size needs: $text-muted measures 2.7:1 against the
   bar's fill and $text-label 4.4:1. The owner asked for the lighter grey twice,
   so the foot takes $text-muted and the measurement is recorded here rather than
   argued against. The reason that is defensible rather than merely allowed is
   what the line is: a caption under a picture, supplementary to a view the
   reader has already looked at, not copy the page has to be read through. If it
   ever needs to be read instead of noticed, $text-label is the middle step at
   4.4:1 and the swap is one value.

   It takes the ground's own quiet step rather than a new grey, and it takes the
   matching step on the other ground with it. $text-muted is the palette's muted
   text on a light ground and $on-dark-muted is the same decision on a dark one,
   so a frame that changes tone changes its ink but not its hierarchy. That also
   puts the foot a step below the address above it, which is the order the two
   should read in: the address is chrome the reader can ignore and the note is
   what the view is showing.

   The padding comes in with it for the same reason: the foot is a band closing
   the frame, and a band whose type has stepped down but whose air has not gets
   quieter in the middle and stays as heavy at the edges.

   The margin is stated rather than inherited. The chapter sits the frame inside
   `prose`, whose own `figcaption` rule hangs captions under the pictures they
   belong to, and this one is inside the frame instead: the margin in that rule
   would push the note away from the edge it is supposed to close. */
.browser-frame-foot {
  font-size: 0.72rem;
  line-height: 1.5;
  margin: 0;
  padding: 0.6rem 1rem;
}

/* The three dots, in the traffic-light colours every desktop browser draws. They
   are the one place on the site that is not our palette, because they are not a
   colour decision: they are a depiction of another window's title bar, and a grey
   row of dots would read as a wireframe while the brand blue would say the frame
   is ours. The three are declared in _tokens.scss with that reason. */
.browser-frame-dots {
  display: flex;
  flex: 0 0 auto;
  gap: 0.35rem;

  i {
    border-radius: 50%;
    display: block;
    height: 0.6rem;
    width: 0.6rem;

    &:nth-child(1) {
      background: $browser-dot-close;
    }

    &:nth-child(2) {
      background: $browser-dot-minimise;
    }

    &:nth-child(3) {
      background: $browser-dot-zoom;
    }
  }
}

/* The address, with the connection mark in front of it. The lock and the scheme
   are the two things a browser shows beside a secure address, so the bar shows
   them the way a reader has already been taught to read them. They sit inside
   the address group so the bar's own gap falls between the dots and the address
   rather than between three parts of one string, and the group is what may
   shrink, so the host is the only part that gives way. */
.browser-frame-address {
  align-items: center;
  display: flex;
  font-family: $font-mono;
  font-size: 0.72rem;
  gap: 0.3rem;
  letter-spacing: 0.01em;
  min-width: 0;
}

/* The connection marks. They carry no colour of their own: each ground's block
   above names the green it takes, because the same green does not read on both
   bars. */
.browser-frame-lock {
  flex: 0 0 auto;
  font-size: 0.62rem;
}

.browser-frame-scheme {
  flex: 0 0 auto;
}

.browser-frame-url {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The live badge, at the far end of the bar from the address.

   `margin-left: auto` is what puts it there and it is the whole of the layout:
   the address is the only child that may shrink, so pushing the badge right
   takes the space out of the host rather than out of the badge, which is the
   trade the bar wants. The host is the part a reader can already see is being
   cut off, and the badge is the part that has to stay whole.

   It is set in the label style the empty slot uses - uppercase, the label size
   and the label tracking - because it is a status rather than a sentence, and
   that is the style the frame already has for a status. The weight is the one
   step this file takes off that style: bold rather than the label weight, by
   the owner's request, so the badge is the loudest thing in a bar whose other
   three parts are deliberately quiet.

   The ink is not set here. Each tone's block above names it with the lock and
   the scheme, because the bar's green is one decision about one connection. */
.browser-frame-live {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  font-size: $label-size;
  font-weight: 700;
  gap: 0.4rem;
  letter-spacing: $label-tracking;
  margin-left: auto;
  text-transform: uppercase;
}

/* The box the picture or the page sits in, and the one element that owns its
   height. It is a property of the frame rather than of what fills it, because
   the frame has three states - a poster, a live page, and a slot with nothing in
   it yet - and all three have to be the same size or the page would reflow as a
   screenshot is supplied.

   The height is the band's, not the client page's: a framed page does not report
   its own, so this box has to be decided here. It is read from `--frame-height`,
   which the caller writes through the `height` prop, and the fallback is the
   whole-window default the live band uses. The prop is a length rather than a
   step because the two callers want different boxes of the same frame: the live
   band wants a window, 48rem, and a story chapter wants a screenshot crop beside
   its prose. A caller that passes one length gets it at every width, and a
   caller that passes nothing gets the desktop window above and the shorter one
   below, which is the pair the live band has always had.

   The height belongs to the box rather than to any one view, so it is the same
   at all three widths the View in control offers, and that is what makes the
   control worth having: 48rem at the desktop width is a window, and the same
   48rem at the phone width is a phone, which is the proportion a reader already
   reads a screenshot in. A height that changed per view would be a second thing
   moving at once and would make the two narrow views look like crops.

   48rem is the tallest box that still fits one screen at a laptop height, so the
   window is whole when the band arrives rather than a pane the reader has to
   scroll to see the bottom of. */
.browser-frame-view {
  background: $ground-accent;
  height: var(--frame-height, 48rem);
  position: relative;

  @media (max-width: 767.98px) {
    height: var(--frame-height, 24rem);
  }
}

/* The crop, shared by the poster and the frame so they land on the same pixels
   and the handover is invisible. It is the top of the page: the screenshot is a
   full-page capture and its top is the part that recognises the site. */
.browser-frame-poster {
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: top;
  width: 100%;
}

/* The frame fills the box and covers the poster once it has content. */
.browser-frame-page {
  border: 0;
  height: 100%;
  inset: 0;
  position: absolute;
  width: 100%;
}

/* The zoom control, over the whole picture.

   It is one box rather than a small mark so the whole screenshot is the target,
   which is why the box itself stays bare at rest: nothing is drawn and the mark
   inside it is what the reader sees. It is the pointer's cursor that says the
   box is a control at all before the pointer has stopped moving, and `zoom-in`
   is the one cursor that already means "this will come closer".

   Once the pointer is on it the box lays a scrim under the mark, because that is
   the one thing the picture cannot do for it: a screenshot is busy in every
   corner, and a pill set on one is competing with whatever it happens to be
   sitting on. The scrim is `$ground-black`, which is the guard's own value at
   the guard's own strength: both are a sheet laid over a picture, so the frame
   quiets a screen one way rather than two. It takes the mark's step, so the two
   arrive together rather than the mark landing on an unchanged picture.

   The scrim is the zoom control's and not the pill's, which is why the portfolio
   card keeps the same pill without one. That card's control says "View project"
   and navigates to the case page, so quietening its picture would promise a look
   closer than pressing it gives. The pill is shared between the two; the claim
   that the picture under it will come closer is this control's alone.

   It follows the pointer rather than the mark, so unlike the mark it is not drawn
   outright below lg. A mark has to be discoverable on a screen with no hover, and
   a scrim that never lifted would quieten every screenshot on the page at once.

   `z-index: 1` puts it above the poster and below the guard, which is `z-index:
   2`. The two never share a frame in practice - a guard is only drawn over a
   framed page and this is only drawn over a still - but the order is stated so
   that a frame which somehow had both would still have its guard on top rather
   than a picture control offering to open a site the reader has not unlocked.

   The ring is inset, because the frame's own `overflow: hidden` would clip one
   that hung beyond the box. */
.browser-frame-zoom {
  background: rgba($ground-black, 0);
  cursor: zoom-in;
  inset: 0;
  position: absolute;
  transition: background-color $dur $ease;
  z-index: 1;

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: -3px;
  }
}

/* The scrim, laid down on the same step the mark arrives on. Keeping the base
   value at zero alpha rather than "transparent" is what lets the one transition
   carry it: a keyword does not interpolate against a colour, and the reveal would
   snap in while the mark faded. */
.browser-frame-zoom:hover,
.browser-frame-zoom:focus-visible {
  background: rgba($ground-black, .3);
}

/* The mark, and the site's reveal pattern: centred, hidden until the pointer is
   over the picture, and shown outright below lg where there is no hover to
   reveal it with.

   It is the same treatment the portfolio card gives its own action, in the same
   two colours: the dark pill stands on any picture at rest, and it takes the
   accent the moment the picture is hovered, so the reader's own pointer is what
   turns it into a call to action. The pill is the page's, not this component's,
   and the card is where it was first written - a second pill in a different
   colour on the same screenshots two pages apart would be the drift this file's
   neighbours all warn about.

   `width: max-content` is stated for the reason the card states it: an
   absolutely positioned box with `left: 50%` and no width is measured against
   the half of the frame to its right, so a longer label would break onto two
   lines near the right edge. */
.browser-frame-zoom-mark {
  align-items: center;
  background: $ground-dark;
  border-radius: $r-sm;
  color: $on-dark-strong;
  display: inline-flex;
  font-family: $headings-font-family;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.45rem;
  left: 50%;
  opacity: 0;
  padding: 0.55rem 1.05rem;
  position: absolute;
  top: 50%;
  transform: translate(-50%, calc(-50% + 0.5rem));
  transition: background-color $dur $ease, opacity $dur $ease, transform $dur $ease;
  white-space: nowrap;
  width: max-content;

  svg {
    font-size: 0.8rem;
  }

  @media (max-width: 991.98px) {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

.browser-frame-zoom:hover .browser-frame-zoom-mark,
.browser-frame-zoom:focus-visible .browser-frame-zoom-mark {
  background: $secondary;
  opacity: 1;
  transform: translate(-50%, -50%);
}

/* Where the empty view's slot sits, which is the half of that state the frame
   owns. The slot draws itself; this insets it from the window's edge so the
   ground around it stays visible and it reads as sitting inside the window
   rather than being the window. A frame with no picture is therefore still a
   window with chrome and a hairline, which is what the caller supplied. */
.browser-frame-blank {
  inset: 0.75rem;
  position: absolute;
}

/* The guard sheet. Absolute over the whole view rather than sized to the poster
   or the frame, so it covers whichever of the two is showing and cannot be
   escaped by a page that renders at another height.

   It is in flow order last and above the frame, so it is what the pointer meets
   over every pixel of the box. That is the whole mechanism: nothing is set on
   the frame itself, because a cross-origin document cannot be told to ignore a
   wheel or a drag and a rule that cannot be withdrawn is not a lock. */
.browser-frame-guard {
  align-items: center;
  background: rgba($ground-black, .3);
  display: flex;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 2;
}

/* The guard fades rather than snapping, on the same $dur the rest of the site's
   state changes take. It is the frame's only animated state and it is short
   enough to read as the sheet being laid down and lifted rather than a second
   event arriving after the press. */
.browser-frame-guard-enter-active,
.browser-frame-guard-leave-active {
  transition: opacity $dur $ease;
}

.browser-frame-guard-enter-from,
.browser-frame-guard-leave-to {
  opacity: 0;
}
</style>

<script>
import { faLock, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import ScreenshotSlot from '@/Components/ScreenshotSlot.vue';
import StatusDot from '@/Components/StatusDot.vue';

FontAwesomeLibrary.add(faLock, faMagnifyingGlass);

export default {
  components: {
    AppButton,
    ScreenshotSlot,
    StatusDot
  },

  emits: ['zoom'],

  props: {
    /* The address the bar prints. It is a prop rather than read from `src`
       because the bar shows the address the site is known by, which is not
       always the URL the frame is loaded from, and the still has no URL at all.
       It carries no scheme: the bar prints that itself. */
    url: {
      type: String,
      required: true
    },
    /* The screenshot, at the path the catalog holds. It is the poster when
       there is a frame and the picture itself when there is not.

       It may be empty, and that is a state rather than a mistake: a screenshot
       that has not been supplied yet is handed over as an empty string, and the
       frame draws its empty slot instead of requesting a file that is not there.
       A frame with neither this nor `src` is nothing but chrome and a slot, which
       is what a case page's story chapters look like on the day it is written. */
    image: {
      type: String,
      default: ''
    },
    /* Only used when there is no `src`, where the screenshot is the content and
       has to describe itself. */
    imageAlt: {
      type: String,
      default: ''
    },
    /* The page to frame. Absent for a still. */
    src: {
      type: String,
      default: ''
    },
    /* The frame's accessible name. A title element is not an option inside an
       iframe, so this is the only label a screen reader gets for the inner page. */
    frameTitle: {
      type: String,
      default: ''
    },
    /* What the view shows, printed in the foot of the window. It is the caller's
       sentence rather than one of this component's, because it describes what the
       caller put in the frame: the case page hands over the caption the chapter
       holds for its screen, and a caller that frames a site has nothing to say
       here and passes nothing. An empty string is therefore the live band, which
       is a frame with no foot rather than a foot with no text. */
    caption: {
      type: String,
      default: ''
    },
    /* Whether the address is served over TLS, which is what draws the lock and
       the scheme in the bar. A property of the address rather than of the frame,
       so it is passed in; it defaults to true because every site this frame has
       ever held is https, and an address that is not would pass false rather
       than keep a mark it has not earned. */
    secure: {
      type: Boolean,
      default: true
    },
    /* Whether the picture in the frame can be opened at its own size. It is the
       caller's to decide because only the caller knows whether there is a larger
       version to open: a case page holds the run of captures its chapter belongs
       to and can offer the next one, and a frame standing on its own has nowhere
       to send the reader. The frame draws the affordance and emits `zoom`; what
       opens is the caller's, because a viewer of a set is the page's business
       and not one picture's.

       It defaults to false, so every existing caller is unchanged and a frame
       that has not asked for the control never grows one. */
    zoomable: {
      type: Boolean,
      default: false
    },
    /* light | dark: the frame's own set. It chooses the chrome bar, the ink in
       it and the shadow together, so the frame is one object on either ground.
       The case page passes light, because it stands the frame on a dark band and
       a dark bar there would have had nothing to contrast against. */
    tone: {
      type: String,
      default: 'light'
    },
    /* How wide the frame is allowed to get. It is 100% of its container up to
       this length, so it is a ceiling rather than a width and a narrow viewport
       is never overflowed. The default leaves it uncapped.

       A caller that moves this value should pass lengths for every state: a
       transition cannot interpolate a percentage against a length, so a move
       between "100%" and "768px" would snap instead of animating. That is why
       the case page's widest mode is a very wide length rather than "100%". */
    width: {
      type: String,
      default: '100%'
    },
    /* How tall the box is. It is a length rather than a step, and passing one
       pins it at every width; leaving it empty keeps the pair the live band has
       always had, which is the whole window on a desktop and the shorter one
       below. The story chapters pass a crop, because a screenshot read beside a
       paragraph is a detail of a page rather than a window to scroll.

       The two dimensions are one prop each rather than a size step because they
       are decided against different things: the width is the band's, and the
       height is what the picture is of. */
    height: {
      type: String,
      default: ''
    }
  },

  /* The frame's own copy. It is looked up here rather than passed in because the
     guard belongs to this component: a caller that frames a site gets the lock
     whether or not it thought to translate a label for it, and the frame is
     drawn on more than one page. The empty view's word has moved out with the
     empty view: the slot is a state two components can be in, so it carries its
     own wording and the frame no longer declares a label it does not draw. The
     live badge's word is here for the same reason as the guard's, and it belongs
     to the frame for one more: what the frame is holding is the frame's own
     business, so no caller has to say "live" for the badge to appear.

     "Click to unlock" rather than "Click to interact". The button is not only
     saying that the frame becomes usable, it is naming the state the frame is in
     while the scrim is over it, and the mark on the button says which way it
     goes.

     "Live" is the state the badge names, and it is translated rather than
     stamped as an English word: the badge is a status the reader has to
     understand on a page that is otherwise in their language. It prints
     uppercase through the style block rather than being written that way here,
     so a language whose own word for it is not Latin still gets its own
     spelling.

     "View full size" is the zoom mark's word, and it is here for the same reason
     the other two are: it names what the frame's own picture does when it is
     pressed, so the caller that opened the viewer does not have to hand over a
     label for a control it did not draw. */
  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Click to unlock': 'Click to unlock',
          Live: 'Live',
          'View full size': 'View full size'
        },
        'zh-MY': {
          'Click to unlock': '点击解除锁定',
          Live: '实时',
          'View full size': '查看原图'
        },
        'ms-MY': {
          'Click to unlock': 'Klik untuk buka kunci',
          Live: 'Langsung',
          'View full size': 'Lihat saiz penuh'
        }
      }
    });

    return { t };
  },

  computed: {
    /* The two dimensions, as the inline style the box reads them from. The width
       is a real property and the height is a custom property, because the height
       has to be readable at two breakpoints and a custom property is the only
       value a media query can fall back from: an inline `height` would have
       outranked the phone rule and pinned the box at one size on every screen. */
    frameStyle() {
      const style = { maxWidth: this.width };

      if (this.height) {
        style['--frame-height'] = this.height;
      }

      return style;
    }
  },

  data() {
    return {
      /* A framed page starts inert. A live site inside a page that scrolls is
         the one thing on this site a reader can start operating without meaning
         to: the pointer crosses it on the way to the next band, the wheel lands
         on it, and a drag inside it paints over the page instead of selecting
         text. So the frame is off until it is asked for.

         `loaded` is the same decision for the request itself. The client's
         page is not fetched while the guard is up, because this page is
         indexed and a frame that has already asked for the client site is
         read as part of it. */
      locked: true,
      loaded: false
    };
  },

  /* The listeners are on the document and the window, so they outlive the
     component unless they are taken off here. Arming happens in `unlock` and
     only there, which is why this can be unconditional: a frame that has never
     been unlocked has nothing to disarm. */
  beforeUnmount() {
    this.disarm();
  },

  methods: {
    unlock() {
      if (!this.src || !this.locked) {
        return;
      }

      this.loaded = true;
      this.locked = false;
      this.arm();
    },

    /* Idempotent, because four different things call it and two of them can
       arrive from one gesture: the frame goes back to inert the moment it stops
       being the thing under the pointer, and it does not matter which of them
       noticed first. */
    relock() {
      if (this.locked) {
        return;
      }

      this.locked = true;
      this.disarm();
    },

    /* Armed only while the frame is live, and only on what can happen outside
       it. Nothing inside the framed page is observable from here, which is
       exactly why the frame is relocked from the edges inwards: the events this
       document can still see are the ones that mean the pointer has left. */
    arm() {
      document.addEventListener('mousedown', this.onAway, true);
      document.addEventListener('focusin', this.onAway, true);
      window.addEventListener('scroll', this.relock, true);
    },

    disarm() {
      document.removeEventListener('mousedown', this.onAway, true);
      document.removeEventListener('focusin', this.onAway, true);
      window.removeEventListener('scroll', this.relock, true);
    },

    /* A press or a focus that landed anywhere but on the frame. Both arrive in
       the capture phase, so they are seen before the target's own handler and
       the frame is already inert by the time the gesture completes.

       Containment is asked of the element by ref rather than of `$el`, for the
       reason the launcher's own away-detection spells out: `$el` is the root
       node, and a template whose first root is a comment gets a fragment whose
       `$el` is an empty text node, which contains nothing. This template has one
       element root today and would still work, but the two are the same check
       and only one of them survives a comment. */
    onAway(event) {
      const chrome = this.$refs.chrome;

      if (chrome && chrome.contains(event.target)) {
        return;
      }

      this.relock();
    }
  }
};
</script>
